// Third Party
import React, { ReactElement, useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { useParams } from 'react-router-dom';

// Components
import { Button, Loader, Typography, Switch } from 'components';
import { ButtonBar, TutorialButton } from 'components/machine-health';

// Types
import { WidgetTableDataItem } from 'types/machine-health';
import { MachineHealthTabs } from 'types/protein';

// Providers
import { useLanguage } from 'providers';

// API
import {
  useGetMachineConfiguratorDataQuery,
  useUpdateMachineConfiguratorDataMutation
} from 'api';

// Constants
import { tutorialPlaceholderStepsData } from 'constants/machineConfig';

// Helpers
import { ToastMsg } from 'common/components/Toast';

// Hooks
import { useCopyConfiguration } from '../../hooks';

// Common
import CopyConfigurationActions from '../../components/CopyConfigurationActions';

// Styling
const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding-left: 6rem;
  padding-right: 6rem;
  width: 100%;
`;

interface Props {
  hasWidgets?: boolean;
  isDirty: boolean;
  setIsDirty: React.Dispatch<React.SetStateAction<boolean>>;
}

const MachineConfigAlarms = ({
  hasWidgets,
  isDirty,
  setIsDirty
}: Props): ReactElement => {
  const theme = useTheme();
  const { machineId } = useParams<{ machineId: string }>();
  const { languageId } = useLanguage();

  const {
    data,
    isFetching: fetching,
    error
  } = useGetMachineConfiguratorDataQuery({
    machineId,
    labels: [MachineHealthTabs.Alarms],
    languageId: languageId,
    showInactive: true
  });

  const [updateProductCleaningData, { isLoading: updating, error: errorUpdate }] =
    useUpdateMachineConfiguratorDataMutation();

  const [tabData, setTabData] = useState<WidgetTableDataItem | undefined>(data?.[0]);
  const [isActive, setIsActive] = useState(!!data?.[0]?.active);

  const {
    copiedConfiguration,
    copyModalOpen,
    copyFetching,
    updatingConfiguration,
    errorCopy,
    errorUpdateConfiguration,
    setCopyModalOpen,
    copyConfiguration,
    onCopyFromSelectedMachine,
    openCopyModal
  } = useCopyConfiguration({
    tabData,
    setTabData,
    setIsDirty,
    machineHealthTabLabel: MachineHealthTabs.Alarms
  });

  const saveInfo = async () => {
    // Do nothing if we have no tab data
    if (!tabData) {
      return;
    }
    // Reconstruct data to send to server
    const updatedProductProcessingTab = {
      ...tabData,
      active: isActive
    };

    await updateProductCleaningData({
      machineId,
      widget: updatedProductProcessingTab,
      languageId: languageId
    })
      .unwrap()
      .then(() => {
        ToastMsg({ heading: 'Success', type: 'success', message: `Alarms updated successfully` });
      })
      .catch((error) => {
        ToastMsg({ heading: 'Error', type: 'error', message: `Failed to update alarms` });
        console.error(error?.data?.detail);
      });
    setIsDirty(false);
  };

  useEffect(() => {
    setTabData(data?.[0]);
    setIsActive(!!data?.[0]?.active);
  }, [data]);

  useEffect(() => {
    setIsDirty(false);
  }, []);

  const handleEnableClick = () => {
    setIsActive((prev) => !prev);
    setIsDirty(true);
  };

  const isFetching = fetching || updating || copyFetching || updatingConfiguration;
  const isError = error || errorUpdate || errorCopy || errorUpdateConfiguration;

  if (isFetching) {
    return <Loader />;
  }

  if (isError) {
    console.error(isError);
    <Typography color="negativeRed">Failed to load data</Typography>;
  }

  return (
    <Container>
      <ButtonBar columnGap="1.5rem" title="" padding="1.375rem 0">
        {tabData && !copiedConfiguration && (
          <>
            <Switch
              checked={isActive}
              handleDiameter={22}
              height={14}
              onChange={handleEnableClick}
              width={40}
              offColor={theme.colors.mediumGrey2}
              offHandleColor={theme.colors.mediumGrey3}
            />

            <Button
              disabled={!isDirty}
              onClick={() => saveInfo()}
              variant={'primary'}
              width="fit-content"
            >
              Save
            </Button>
          </>
        )}

        <CopyConfigurationActions
          copiedConfiguration={copiedConfiguration}
          copyConfiguration={copyConfiguration}
          copyModalOpen={copyModalOpen}
          hasWidgets={hasWidgets}
          onCopyFromSelectedMachine={onCopyFromSelectedMachine}
          openCopyModal={openCopyModal}
          setCopyModalOpen={setCopyModalOpen}
        />
        <TutorialButton stepsData={tutorialPlaceholderStepsData} />
      </ButtonBar>
    </Container>
  );
};

export default MachineConfigAlarms;
